import React from 'react';
import { ArrowRight, Pencil } from 'lucide-react';

// Mobile only. Thin sticky summary of the current search that slides in once the
// full search panel has scrolled out of view. Tap it to bring the panel back.

const stripSuffix = (name = '') =>
    String(name)
        .replace(/‎/g, '')
        .replace(/\s*(railway station|airport)\s*$/i, '')
        .trim();

const formatDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (isNaN(dt)) return '';
    return dt.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

const formatPax = ({ adults = 1, children = 0, infants = 0 } = {}) => {
    const total = Number(adults) + Number(children) + Number(infants);
    if (total === 1) return '1 adult';
    if (!children && !infants) return `${adults} adults`;
    return `${total} pax`;
};

const formatModes = (modes = []) => {
    if (!modes.length || modes.includes('All') || modes.length >= 3) return 'All modes';
    return modes.join(' + ');
};

const formatClass = (seatClasses = [], modes = []) => {
    const trainOnly = modes.length === 1 && modes[0] === 'Train';
    const flightOnly = modes.length === 1 && modes[0] === 'Flight';
    const trainClass = seatClasses.find(c => ['1A', '2A', '3A', 'SL'].includes(c));
    const flightClass = seatClasses.find(c => ['Economy', 'Premium', 'Business', 'First'].includes(c));
    if (trainOnly) return trainClass || '';
    if (flightOnly) return flightClass || '';
    return [trainClass, flightClass].filter(Boolean).join('/');
};

const CompactSearchBar = ({
    visible,
    from,
    to,
    date,
    travelerCounts,
    seatClasses,
    modes,
    isLoading,
    onExpand,
}) => {
    const meta = [
        formatDate(date),
        formatPax(travelerCounts),
        formatClass(seatClasses, modes),
        formatModes(modes),
    ].filter(Boolean).join(' · ');

    return (
        <div
            className={`md:hidden fixed inset-x-0 top-0 z-40 transition-transform duration-200 ease-out will-change-transform ${visible ? 'translate-y-0' : '-translate-y-full pointer-events-none'}`}
            aria-hidden={!visible}
        >
            <button
                type="button"
                onClick={onExpand}
                tabIndex={visible ? 0 : -1}
                aria-label="Edit search"
                className="w-full bg-white/95 backdrop-blur border-b border-slate-200 shadow-[0_4px_12px_-6px_rgba(15,23,42,0.25)] pt-[env(safe-area-inset-top)] text-left active:bg-slate-50"
            >
                <div className="flex items-center gap-3 px-4 h-12">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900 leading-tight">
                            <span className="truncate max-w-[42%]">{stripSuffix(from) || 'From'}</span>
                            <ArrowRight size={13} className="shrink-0 text-orange-500" aria-hidden="true" />
                            <span className="truncate max-w-[42%]">{stripSuffix(to) || 'To'}</span>
                        </div>
                        <p className="mt-0.5 text-[11px] text-slate-500 truncate leading-tight">{meta}</p>
                    </div>
                    <span className="shrink-0 w-8 h-8 rounded-full bg-orange-50 border border-orange-200 text-orange-600 flex items-center justify-center" aria-hidden="true">
                        <Pencil size={14} />
                    </span>
                </div>

                {/* Thin progress line while results are still streaming */}
                <div className="relative h-0.5 overflow-hidden" aria-hidden="true">
                    {isLoading && <div className="compact-bar-progress absolute inset-y-0 w-1/3 bg-orange-500" />}
                </div>
            </button>
        </div>
    );
};

export default CompactSearchBar;
